import { ShareIcon, TrashIcon } from "@heroicons/react/24/outline";
import { getAuth } from "firebase/auth";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import remarkGfm from "remark-gfm";
import Share from "../matches/Share";
import { reportMarkdownComponents } from "../matches/reportMarkdownComponents";
import { sendShareNotification } from "../utils/emailNotify";
import { base64ToBlobUrl } from "../utils/reportPdf";
import {
    assignPlaylist,
    deleteSavedReport,
    fetchAllMyPlaylists,
    fetchAssignedPlaylists,
    fetchReportFile,
    fetchReportsSharedWithMe,
    fetchSavedReports,
    fetchSharedReportFile,
    loadMatchesWithBuffers,
    loadPlaylistState,
    loadSessionState,
    saveReport,
    shareReport,
    unassignPlaylist,
} from "../utils/teamPlayerData";

function makeDate(seconds) {
  if (!seconds) return "";
  try {
    return new Date(seconds * 1000).toLocaleDateString();
  } catch {
    return "";
  }
}

// A bordered card with a sticky header and a scrollable body.
function Panel({ title, action, children }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col rounded-md border border-base-300">
      <div className="flex items-center justify-between border-b border-base-300 px-3 py-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {action}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">{children}</div>
    </div>
  );
}

// ---- Matches --------------------------------------------------------------

export function MatchesPanel({ matches, t }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const openMatch = async (match) => {
    if (busy) return;
    setBusy(true);
    try {
      const state = await loadSessionState(match.id);
      if (state) {
        navigate("/session", { state });
      } else {
        alert(t("Could not load this match."));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title={`${t("Matches")} (${matches.length})`}>
      {matches.length === 0 ? (
        <p className="p-2 text-sm text-base-content/60">{t("No matches.")}</p>
      ) : (
        <ul className="flex flex-col gap-1">
          {matches.map((m) => (
            <li key={m.id}>
              <button
                onClick={() => openMatch(m)}
                className="w-full rounded px-2 py-1 text-left text-sm hover:bg-base-200"
              >
                <span className="font-medium">
                  {m.teamA} <span className="text-base-content/50">vs</span>{" "}
                  {m.teamB}
                </span>
                <span className="ml-2 text-xs text-base-content/60">
                  {m.tournament ? `${m.tournament} · ` : ""}
                  {makeDate(m.sessionDateTimeInSeconds)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ---- Playlists ------------------------------------------------------------

export function PlaylistsPanel({ scope, refId, t }) {
  const navigate = useNavigate();
  const [assigned, setAssigned] = useState([]);
  const [loading, setLoading] = useState(false);
  const [picker, setPicker] = useState(false);
  const [available, setAvailable] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [search, setSearch] = useState("");

  const reload = async () => {
    if (!refId) {
      setAssigned([]);
      return;
    }
    setLoading(true);
    try {
      setAssigned(await fetchAssignedPlaylists(scope, refId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, refId]);

  const openPicker = async () => {
    setPicker(true);
    setSearch("");
    setAvailableLoading(true);
    try {
      setAvailable(await fetchAllMyPlaylists());
    } finally {
      setAvailableLoading(false);
    }
  };

  const doAssign = async (pl) => {
    await assignPlaylist(scope, refId, pl);
    await reload();
  };

  const doUnassign = async (a) => {
    await unassignPlaylist(a.id);
    await reload();
  };

  const openPlaylist = async (a) => {
    const state = await loadPlaylistState(a.playlistId);
    if (state) {
      navigate("/playlist", { state });
    } else {
      alert(t("Could not load this playlist."));
    }
  };

  const assignedIds = new Set(assigned.map((a) => a.playlistId));
  const term = search.trim().toLowerCase();
  const filteredAvailable = term
    ? available.filter((p) =>
        `${p.description ?? ""} ${p.tags ?? ""}`.toLowerCase().includes(term)
      )
    : available;

  return (
    <>
      <Panel
        title={`${t("Playlists")} (${assigned.length})`}
        action={
          <button
            className="btn btn-xs btn-ghost"
            onClick={openPicker}
            disabled={!refId}
          >
            {t("Assign")}
          </button>
        }
      >
        {loading ? (
          <p className="p-2 text-sm text-base-content/60">{t("Loading…")}</p>
        ) : assigned.length === 0 ? (
          <p className="p-2 text-sm text-base-content/60">
            {t("No playlists assigned.")}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {assigned.map((a) => (
              <li key={a.id} className="flex items-center gap-1">
                <button
                  onClick={() => openPlaylist(a)}
                  className="flex-1 rounded px-2 py-1 text-left text-sm hover:bg-base-200"
                >
                  <span className="underline">
                    {a.description || t("Untitled playlist")}
                  </span>
                  {a.tags ? (
                    <span className="ml-2 text-xs text-base-content/60">
                      {a.tags.split("|").filter(Boolean).join(", ")}
                    </span>
                  ) : null}
                </button>
                <button
                  className="btn btn-ghost btn-xs"
                  title={t("Remove")}
                  onClick={() => doUnassign(a)}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {picker && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="mb-2 text-lg font-bold">{t("Assign playlist")}</h3>
            <input
              type="text"
              className="input-generic mb-2"
              placeholder={t("Search playlists…")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="max-h-[50vh] overflow-y-auto">
              {availableLoading ? (
                <p className="p-2 text-sm text-base-content/60">
                  {t("Loading…")}
                </p>
              ) : filteredAvailable.length === 0 ? (
                <p className="p-2 text-sm text-base-content/60">
                  {t("No playlists found.")}
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {filteredAvailable.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-center justify-between gap-2 rounded px-2 py-1 hover:bg-base-200"
                    >
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {p.description || t("Untitled playlist")}
                        </div>
                        {p.tags ? (
                          <div className="truncate text-xs text-base-content/60">
                            {p.tags.split("|").filter(Boolean).join(", ")}
                          </div>
                        ) : null}
                      </div>
                      <button
                        className="btn btn-xs btn-primary"
                        disabled={assignedIds.has(p.id)}
                        onClick={() => doAssign(p)}
                      >
                        {assignedIds.has(p.id) ? t("Assigned") : t("Assign")}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="modal-action">
              <button className="btn btn-sm" onClick={() => setPicker(false)}>
                {t("Close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---- Reports --------------------------------------------------------------

export function ReportsPanel({ scope, refId, matches, reportLabel, t }) {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [openingAI, setOpeningAI] = useState(false);
  const [sharing, setSharing] = useState(null);
  const [shareNonce, setShareNonce] = useState(0);

  const reload = async () => {
    if (!refId) {
      setReports([]);
      return;
    }
    setLoading(true);
    try {
      setReports(await fetchSavedReports(scope, refId));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, refId]);

  const doSave = async () => {
    if (!content.trim()) return;
    await saveReport(scope, refId, { title, content, kind: "custom" });
    setAdding(false);
    setTitle("");
    setContent("");
    await reload();
  };

  const doDelete = async (r) => {
    if (!window.confirm(t("Delete this report?"))) return;
    await deleteSavedReport(r);
    await reload();
  };

  const doShareReport = (r) => {
    setShareNonce((n) => n + 1);
    setSharing(r);
  };

  const doDoShare = async (share) => {
    const report = sharing;
    setSharing(null);
    if (!share || !report) return;
    const ok = await shareReport(report, share);
    if (!ok) {
      alert(t("Could not share the report. Please try again."));
      return;
    }
    if (share.notifyByEmail && share.shareStatus === 2) {
      sendShareNotification({
        to: share.shareUsers,
        itemType: "report",
        itemTitle: report.title,
        sharedByEmail: getAuth().currentUser?.email,
      });
    }
    await reload();
  };

  const openReport = async (r) => {
    setViewing(r);
    setPdfUrl(null);
    if (r.kind === "pdf") {
      setLoadingFile(true);
      try {
        const b64 = await fetchReportFile(r.id);
        setPdfUrl(b64 ? base64ToBlobUrl(b64) : null);
      } finally {
        setLoadingFile(false);
      }
    }
  };

  const closeViewing = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setViewing(null);
  };

  const openAIReports = async () => {
    if (openingAI || !matches || matches.length === 0) return;
    setOpeningAI(true);
    try {
      const xms = await loadMatchesWithBuffers(matches);
      if (xms.length === 0) {
        alert(t("Could not load stats for these matches."));
        return;
      }
      navigate("/aireports", { state: { matches: xms, team: reportLabel } });
    } finally {
      setOpeningAI(false);
    }
  };

  return (
    <>
      <Panel
        title={`${t("Reports")} (${reports.length})`}
        action={
          <div className="flex gap-1">
            <button
              className="btn btn-xs btn-ghost"
              onClick={openAIReports}
              disabled={!matches || matches.length === 0 || openingAI}
              title={t("Generate an AI report from these matches")}
            >
              {t("AI Reports")}
            </button>
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => setAdding(true)}
              disabled={!refId}
            >
              {t("Add")}
            </button>
          </div>
        }
      >
        {loading ? (
          <p className="p-2 text-sm text-base-content/60">{t("Loading…")}</p>
        ) : reports.length === 0 ? (
          <p className="p-2 text-sm text-base-content/60">
            {t("No reports saved.")}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {reports.map((r) => (
              <li key={r.id} className="flex items-center gap-1">
                <button
                  onClick={() => openReport(r)}
                  className="flex-1 rounded px-2 py-1 text-left text-sm hover:bg-base-200"
                >
                  <span className="underline">{r.title}</span>
                  {r.kind === "pdf" && (
                    <span className="badge badge-xs badge-outline ml-2 align-middle">
                      PDF
                    </span>
                  )}
                  <span className="ml-2 text-xs text-base-content/60">
                    {makeDate(r.createdAtSeconds)}
                  </span>
                </button>
                <button
                  className="btn btn-ghost btn-xs"
                  title={t("Share")}
                  onClick={() => doShareReport(r)}
                >
                  <ShareIcon className="h-4 w-4" />
                </button>
                <button
                  className="btn btn-ghost btn-xs"
                  title={t("Delete")}
                  onClick={() => doDelete(r)}
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {sharing && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="mb-2 text-lg font-bold">
              {t("Share")} — {sharing.title}
            </h3>
            <Share
              shareStatus={sharing.shareStatus}
              shareUsers={sharing.shareUsers}
              currentTime={shareNonce}
              onShare={doDoShare}
            />
          </div>
        </div>
      )}

      {adding && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="mb-2 text-lg font-bold">{t("Add report")}</h3>
            <input
              type="text"
              className="input-generic mb-2"
              placeholder={t("Report title")}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <textarea
              className="textarea textarea-bordered h-64 w-full rounded-none font-mono text-sm"
              placeholder={t("Paste the report (Markdown supported)…")}
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <div className="modal-action">
              <button
                className="btn btn-sm"
                onClick={() => {
                  setAdding(false);
                  setTitle("");
                  setContent("");
                }}
              >
                {t("Cancel")}
              </button>
              <button
                className="btn btn-sm btn-primary"
                onClick={doSave}
                disabled={!content.trim()}
              >
                {t("Save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {viewing && (
        <div className="modal modal-open">
          <div className="modal-box max-w-3xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-lg font-bold">{viewing.title}</h3>
              <div className="flex gap-1">
                {viewing.kind === "pdf" && pdfUrl && (
                  <a
                    className="btn btn-sm"
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("Open / Download")}
                  </a>
                )}
                <button className="btn btn-sm" onClick={closeViewing}>
                  {t("Close")}
                </button>
              </div>
            </div>
            {viewing.kind === "pdf" ? (
              loadingFile ? (
                <p className="p-4 text-sm text-base-content/60">
                  {t("Loading…")}
                </p>
              ) : pdfUrl ? (
                <iframe
                  title={viewing.title}
                  src={pdfUrl}
                  className="h-[70vh] w-full rounded border border-base-300"
                />
              ) : (
                <p className="p-4 text-sm text-base-content/60">
                  {t("Could not load this report.")}
                </p>
              )
            ) : (
              <div className="prose max-w-none overflow-y-auto max-h-[65vh]">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={reportMarkdownComponents}
                >
                  {viewing.content || ""}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

// ---- Reports shared with me (by other users) ------------------------------

export function SharedReportsPanel({ t }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewingFile, setViewingFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      setReports(await fetchReportsSharedWithMe());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const openReport = async (r) => {
    setViewing(r);
    setPdfUrl(null);
    setViewingFile(null);
    setLoadingFile(true);
    try {
      const file = await fetchSharedReportFile(r.id);
      setViewingFile(file);
      if (r.kind === "pdf" && file?.pdfBase64) {
        setPdfUrl(base64ToBlobUrl(file.pdfBase64));
      }
    } finally {
      setLoadingFile(false);
    }
  };

  const closeViewing = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setViewing(null);
    setViewingFile(null);
  };

  if (!loading && reports.length === 0) return null;

  return (
    <>
      <Panel title={`${t("Shared with me")} (${reports.length})`}>
        {loading ? (
          <p className="p-2 text-sm text-base-content/60">{t("Loading…")}</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {reports.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => openReport(r)}
                  className="w-full rounded px-2 py-1 text-left text-sm hover:bg-base-200"
                >
                  <span className="underline">{r.title}</span>
                  {r.kind === "pdf" && (
                    <span className="badge badge-xs badge-outline ml-2 align-middle">
                      PDF
                    </span>
                  )}
                  <span className="ml-2 text-xs text-base-content/60">
                    {t("Shared by")} {r.ownerEmail}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {viewing && (
        <div className="modal modal-open">
          <div className="modal-box max-w-3xl">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-lg font-bold">{viewing.title}</h3>
              <div className="flex gap-1">
                {viewing.kind === "pdf" && pdfUrl && (
                  <a
                    className="btn btn-sm"
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t("Open / Download")}
                  </a>
                )}
                <button className="btn btn-sm" onClick={closeViewing}>
                  {t("Close")}
                </button>
              </div>
            </div>
            {viewing.kind === "pdf" ? (
              loadingFile ? (
                <p className="p-4 text-sm text-base-content/60">
                  {t("Loading…")}
                </p>
              ) : pdfUrl ? (
                <iframe
                  title={viewing.title}
                  src={pdfUrl}
                  className="h-[70vh] w-full rounded border border-base-300"
                />
              ) : (
                <p className="p-4 text-sm text-base-content/60">
                  {t("Could not load this report.")}
                </p>
              )
            ) : loadingFile ? (
              <p className="p-4 text-sm text-base-content/60">
                {t("Loading…")}
              </p>
            ) : (
              <div className="prose max-w-none overflow-y-auto max-h-[65vh]">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={reportMarkdownComponents}
                >
                  {viewingFile?.content || ""}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
